import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
  Skeleton,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import { ArrowBack, Tune, Policy as PolicyIcon, Map as MapIcon, LiveTv } from '@mui/icons-material';
import { camerasApi, Camera } from '../services/camerasApi';
import { zonesApi, Zone } from '../services/zonesApi';
import { linesApi, Line } from '../services/linesApi';
import { policiesApi, Policy } from '../services/policiesApi';
import { scheduleDeferredWork } from '../utils/deferredWork';

const SITE_ID = process.env.REACT_APP_SITE_ID || 'default';

const TabPanel: React.FC<{ value: number; index: number; children: React.ReactNode }> = ({ value, index, children }) => {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
};

export const CameraWorkspace: React.FC = () => {
  const { cameraId } = useParams<{ cameraId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [camera, setCamera] = useState<Camera | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const cameraWorkspaceFetchGenRef = useRef(0);

  const deviceIdFromQuery = useMemo(() => {
    const q = new URLSearchParams(location.search || '');
    return (q.get('device_id') || '').trim() || '';
  }, [location.search]);

  const resolvedDeviceId = useMemo(() => {
    return String(camera?.edge_device_id || deviceIdFromQuery || '').trim();
  }, [camera?.edge_device_id, deviceIdFromQuery]);

  const fetchAll = useCallback(async () => {
    if (!cameraId) return;
    setLoading(true);
    setError(null);
    try {
      // Camera IDs are NOT globally unique across devices. Disambiguate by device_id query param when present.
      const camsRes = await camerasApi.listCameras(SITE_ID, deviceIdFromQuery || undefined);
      const matches = (camsRes.items || []).filter((c) => c.camera_id === cameraId);

      if (deviceIdFromQuery && matches.length === 0) {
        setCamera(null);
        setZones([]);
        setLines([]);
        setPolicies([]);
        setError(`Camera ${cameraId} not found on device ${deviceIdFromQuery}.`);
        setLoading(false);
        return;
      }

      if (!deviceIdFromQuery && matches.length > 1) {
        // Ambiguous camera_id across devices. Force the caller to specify device_id.
        setCamera(null);
        setZones([]);
        setLines([]);
        setPolicies([]);
        const devs = matches.map((m) => m.edge_device_id).filter(Boolean).join(', ');
        setError(`Camera id ${cameraId} exists on multiple devices. Choose device_id. (${devs})`);
        setLoading(false);
        return;
      }

      const cam = matches[0] || null;
      setCamera(cam);

      const deviceId = String((cam?.edge_device_id || deviceIdFromQuery || '')).trim() || undefined;
      setPolicies([]);
      const [zonesRes, linesRes] = await Promise.all([
        zonesApi.listZones(undefined, undefined, deviceId, cameraId, true),
        linesApi.listLines(deviceId, cameraId, true),
      ]);

      // Always scope zones/lines to the resolved device_id to avoid mixing between two edges
      setZones((zonesRes.zones || []).filter((z) => z.camera_id === cameraId && (!deviceId || z.device_id === deviceId)));
      setLines((linesRes.lines || []).filter((l) => l.camera_id === cameraId && (!deviceId || l.device_id === deviceId)));
      setLoading(false);

      const wsGen = ++cameraWorkspaceFetchGenRef.current;
      const camIdForPolicies = cameraId;
      scheduleDeferredWork(() => {
        if (wsGen !== cameraWorkspaceFetchGenRef.current) return;
        void (async () => {
          try {
            const policiesRes = await policiesApi.listPolicies(SITE_ID, true);
            if (wsGen !== cameraWorkspaceFetchGenRef.current) return;
            const allPolicies = policiesRes.policies || [];
            const filteredPolicies = allPolicies.filter((p: any) => {
              const pid = String((p as any)?.device_id || (p as any)?.edge_device_id || '').trim();
              const camId = String((p as any)?.camera_id || '').trim();
              const scopeCams = Array.isArray((p as any)?.scope?.camera_ids) ? (p as any).scope.camera_ids.map(String) : [];
              const usesCam = camId === camIdForPolicies || scopeCams.includes(camIdForPolicies);
              if (!usesCam) return false;
              if (!deviceId) return true;
              return pid === deviceId;
            });
            setPolicies(filteredPolicies);
          } catch {
            if (wsGen === cameraWorkspaceFetchGenRef.current) setPolicies([]);
          }
        })();
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load camera workspace');
      setLoading(false);
    }
  }, [cameraId, deviceIdFromQuery]);

  useEffect(() => {
    // Allow deep-linking to a tab:
    //   /cameras/<id>?tab=policies|geometry|live
    const q = new URLSearchParams(location.search || '');
    const t = (q.get('tab') || '').toLowerCase();
    if (t === 'geometry') setTab(1);
    else if (t === 'policies') setTab(2);
    else setTab(0);
  }, [location.search]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const enabledZones = useMemo(() => zones.filter((z) => z.enabled !== false), [zones]);
  const enabledLines = useMemo(() => lines.filter((l) => l.enabled !== false), [lines]);

  const policyCounts = useMemo(() => {
    const enabledPolicies = (policies || []).filter((p) => p && p.enabled !== false && p.status !== 'deleted');
    const zoneCount: Record<string, number> = {};
    const lineCount: Record<string, number> = {};
    for (const p of enabledPolicies) {
      for (const zid of p.scope?.zone_ids || []) {
        zoneCount[zid] = (zoneCount[zid] || 0) + 1;
      }
      const lineId = (p as any)?.config?.line_id || (p as any)?.config?.line?.line_id;
      if (p.policy_type === 'line_crossing' && lineId) {
        lineCount[lineId] = (lineCount[lineId] || 0) + 1;
      }
    }
    return { zoneCount, lineCount };
  }, [policies]);

  const openZonesEditor = () => {
    if (!cameraId) return;
    const qs = new URLSearchParams();
    qs.set('camera_id', String(cameraId));
    if (resolvedDeviceId) qs.set('device_id', resolvedDeviceId);
    navigate(`/zones?${qs.toString()}`);
  };

  const openPolicies = () => {
    if (!cameraId) return;
    const qs = new URLSearchParams();
    qs.set('camera_id', String(cameraId));
    if (resolvedDeviceId) qs.set('device_id', resolvedDeviceId);
    navigate(`/policies?${qs.toString()}`);
  };

  const createLinePolicy = (line: Line) => {
    const camId = String((line as any)?.camera_id ?? '');
    const lid = String((line as any)?.line_id ?? '');
    const qs = new URLSearchParams();
    qs.set('create', '1');
    qs.set('type', 'line_crossing');
    qs.set('camera_id', camId);
    qs.set('line_id', lid);
    if (resolvedDeviceId) qs.set('device_id', resolvedDeviceId);
    navigate(`/policies?${qs.toString()}`);
  };

  const createZonePolicy = (zone: Zone, type: string) => {
    const qs = new URLSearchParams();
    qs.set('create', '1');
    qs.set('type', String(type));
    qs.set('camera_id', String(zone.camera_id || cameraId || ''));
    qs.set('zone_id', String(zone.zone_id));
    if (resolvedDeviceId) qs.set('device_id', resolvedDeviceId);
    navigate(`/policies?${qs.toString()}`);
  };

  if (!cameraId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Missing camera id</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/cameras')}>
            Cameras
          </Button>
          <Box>
            <Typography variant="h5">
              Camera Workspace: {camera?.name || cameraId}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                ({cameraId})
              </Typography>
            </Typography>
            {camera?.edge_device_id && (
              <Typography variant="body2" color="text.secondary">
                device: {camera.edge_device_id}
              </Typography>
            )}
          </Box>
        </Box>
        <Button variant="outlined" onClick={fetchAll} startIcon={<Tune />}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
          {!camera && !deviceIdFromQuery && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: open this page as <code>/cameras/{cameraId}?device_id=&lt;edge_device_id&gt;</code> to disambiguate.
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      <Paper sx={{ px: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<LiveTv />} label="Live" />
          <Tab icon={<MapIcon />} label="Geometry" />
          <Tab icon={<PolicyIcon />} label="Policies" />
        </Tabs>

        {loading ? (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="text" width="60%" />
          </Box>
        ) : (
          <>
            <TabPanel value={tab} index={0}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Live is a shortcut view. Use <strong>Live alerts</strong> for the firing-alert board and WebSocket
                updates; use <strong>Live View</strong> for full camera streams.
              </Alert>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    const qs = new URLSearchParams();
                    qs.set('camera_id', String(cameraId));
                    if (resolvedDeviceId) qs.set('device_id', resolvedDeviceId);
                    navigate(`/live?${qs.toString()}`);
                  }}
                >
                  Open Live alerts
                </Button>
                <Button variant="outlined" onClick={openZonesEditor}>
                  Open Zones & Lines
                </Button>
                <Button variant="outlined" onClick={openPolicies}>
                  Open Policies
                </Button>
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Geometry for {cameraId}{' '}
                  <Chip size="small" label={`${enabledZones.length} zones`} sx={{ ml: 1 }} />{' '}
                  <Chip size="small" label={`${enabledLines.length} lines`} sx={{ ml: 1 }} />
                </Typography>
                <Button variant="contained" onClick={openZonesEditor}>
                  Edit geometry
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Lines
              </Typography>
              {enabledLines.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No enabled lines for this camera. Create lines in the Snapshot Editor.
                </Alert>
              ) : (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {enabledLines.map((l) => (
                    <Paper key={l.line_id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1">
                            {l.name} ({l.line_id})
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            points_norm: {JSON.stringify(l.points_norm)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip size="small" label={`Used by: ${policyCounts.lineCount[l.line_id] || 0}`} />
                          <Button size="small" variant="outlined" onClick={() => openPolicies()}>
                            View policies
                          </Button>
                          <Button size="small" variant="contained" onClick={() => createLinePolicy(l)}>
                            Create policy
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Zones
              </Typography>
              {enabledZones.length === 0 ? (
                <Alert severity="info">No enabled zones for this camera.</Alert>
              ) : (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {enabledZones.map((z) => (
                    <Paper key={z.zone_id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1">
                            {z.name} ({z.zone_id})
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            bbox_norm: {JSON.stringify(z.bbox_norm)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <Chip size="small" label={`Used by: ${policyCounts.zoneCount[z.zone_id] || 0}`} />
                          <Button size="small" variant="outlined" onClick={openPolicies}>
                            View policies
                          </Button>
                          <Button size="small" variant="contained" onClick={() => createZonePolicy(z, 'zone_intrusion')}>
                            Create intrusion policy
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Policies for {cameraId}</Typography>
                <Button variant="contained" onClick={openPolicies}>
                  Open policy builder
                </Button>
              </Box>
              <Alert severity="info">
                Policy creation/editing stays in the Policies page. We deep-link from geometry cards to prefill scope/type.
              </Alert>
            </TabPanel>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CameraWorkspace;

